import { useEffect, useState, useCallback } from 'react';
import { Printer, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAuth, canEditIt } from '@/auth/AuthContext';
import { itApi } from './itApi';
import IpEditDrawer from './IpEditDrawer';
import UserVlanSummary from './UserVlanSummary';
import { printIpPlan } from './ipPrint';
import { VlanPills } from './vlan/VlanPills';
import { OverviewTab } from './vlan/OverviewTab';
import { IpListTab } from './vlan/IpListTab';
import { OmadaSyncTab } from './vlan/OmadaSyncTab';
import { IpBulkBar } from './vlan/IpBulkBar';
import { ReserveDialog, VlanEditDialog, VlanCreateDialog, PurposeRangesDialog } from './vlan/VlanDialogs';

export default function VlanManagement() {
  const { user } = useAuth();
  const admin = canEditIt(user);

  const [vlans, setVlans] = useState([]);
  const [activeVlan, setActiveVlan] = useState(null);
  const [allEntries, setAllEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('overview');
  const [mapMode, setMapMode] = useState('state');

  const [selected, setSelected] = useState(new Set());
  const [bulkOwner, setBulkOwner] = useState('');
  const [bulkPurpose, setBulkPurpose] = useState('');

  const [editing, setEditing] = useState(null);       // drawer entry
  const [editingVlan, setEditingVlan] = useState(null);
  const [creatingVlan, setCreatingVlan] = useState(null);
  const [reserve, setReserve] = useState(null);       // {user, device_name, purpose}
  const [sites, setSites] = useState([]);
  const [commands, setCommands] = useState([]);
  const [cmdPending, setCmdPending] = useState(0);
  const [unplaced, setUnplaced] = useState([]);
  const [rangesDialog, setRangesDialog] = useState(null);

  // ── loaders ──────────────────────────────────────────────────────────────
  const refreshVlans = useCallback(() => itApi.vlans().then((raw) => {
    const v = [...raw].sort((a, b) => (a.vlan_id ?? 9999) - (b.vlan_id ?? 9999));
    setVlans(v);
    setActiveVlan((cur) => v.find((x) => x.id === cur?.id) || cur || v[0] || null);
    return v;
  }), []);

  useEffect(() => { refreshVlans(); }, [refreshVlans]);

  const loadEntries = useCallback(() => {
    if (!admin || !activeVlan) return;
    setLoading(true);
    itApi.ips(activeVlan.id).then(setAllEntries).finally(() => setLoading(false));
    setSelected(new Set());
  }, [admin, activeVlan]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const loadCommands = useCallback(() => {
    if (!admin) return;
    itApi.omadaCommands().then((d) => { setCommands(d.commands || []); setCmdPending(d.pending || 0); }).catch(() => {});
  }, [admin]);

  useEffect(() => {
    if (!admin) return undefined;
    loadCommands();
    itApi.omadaSites().then((d) => setSites(d.sites || [])).catch(() => {});
    itApi.unplaced().then(setUnplaced).catch(() => {});
    const t = setInterval(loadCommands, 30000);
    return () => clearInterval(t);
  }, [admin, loadCommands]);

  // ── mutations ─────────────────────────────────────────────────────────────
  const afterChange = () => { loadEntries(); refreshVlans(); itApi.unplaced().then(setUnplaced).catch(() => {}); };

  const saveEdit = async (form) => {
    try {
      await itApi.updateIp(form.id, {
        connection: form.connection || null, user: form.user || null,
        device_name: form.device_name || null, mac_address: form.mac_address || null,
        purpose: form.purpose || null, note: form.note || null, auth_free: !!form.auth_free,
        no_auto_release: !!form.no_auto_release,
      });
      toast.success('保存しました');
      setEditing(null);
      afterChange();
    } catch { toast.error('保存に失敗しました'); }
  };

  const releaseFromDrawer = async (form) => {
    try {
      await itApi.releaseIp(form.id);
      toast.success(`${form.ip_address} を解放しました`);
      setEditing(null);
      afterChange();
    } catch { toast.error('解放に失敗しました'); }
  };

  const writeToOmada = async (form, clearFixed) => {
    try {
      await itApi.omadaWrite(form.id, {
        name: form.device_name || null,
        fixed_ip: clearFixed ? null : (form.fixed_ip || null),
        clear_fixed_ip: clearFixed,
      });
      toast.success('Omadaへの反映を予約しました（次回の同期で適用されます）');
      setEditing(null);
      setTab('sync');
      loadCommands();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Omada反映に失敗しました'); }
  };

  const openEditCell = (entry) => {
    // map empty cells pass a synthetic entry; find the real one by ip
    const real = allEntries.find((e) => e.id === entry.id) || allEntries.find((e) => e.ip_address === entry.ip_address);
    if (real) setEditing(real);
  };

  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      if (action === 'set') {
        await itApi.bulkUpdate({ ids, action: 'set', user: bulkOwner, purpose: bulkPurpose });
      } else {
        await itApi.bulkUpdate({ ids, action: 'release' });
      }
      toast.success(`${ids.length}件を更新しました`);
      setBulkOwner(''); setBulkPurpose('');
      afterChange();
    } catch (e) { toast.error(e?.response?.data?.detail || '一括更新に失敗しました'); }
  };

  const exportSelected = () => {
    const rows = allEntries.filter((e) => selected.has(e.id) && e.status === 'assigned');
    if (!rows.length) { toast.error('割当済みの選択行がありません'); return; }
    printIpPlan([{ name: activeVlan?.name || '', subnet: activeVlan?.subnet || '', total: rows.length, assigned: rows.length, entries: rows }], '選択IPの台帳');
  };

  const submitReserve = async () => {
    try {
      const e = await itApi.reserveNext(activeVlan.id, reserve);
      toast.success(`${e.ip_address} を予約しました`);
      setReserve(null);
      afterChange();
      setEditing(e);
    } catch (err) { toast.error(err?.response?.data?.detail || '空きIPの予約に失敗しました'); }
  };

  const printAll = async () => {
    try {
      const groups = [];
      for (const v of vlans) {
        const rows = (await itApi.ips(v.id)).filter((e) => e.status === 'assigned');
        groups.push({ name: v.name, subnet: v.subnet || '', total: v.total || 0, assigned: rows.length, entries: rows });
      }
      printIpPlan(groups, 'IP管理台帳');
    } catch { toast.error('PDFの生成に失敗しました'); }
  };

  const openRanges = async () => {
    try {
      const d = await itApi.purposeRanges(activeVlan.id);
      setRangesDialog({ rows: (d.ranges || []).map((r) => ({ ...r })) });
    } catch { toast.error('用途レンジの取得に失敗しました'); }
  };
  const saveRanges = async () => {
    try {
      const rows = rangesDialog.rows.filter((r) => r.purpose && r.start !== '' && r.end !== '');
      await itApi.setPurposeRanges(activeVlan.id, { ranges: rows.map((r) => ({ start: Number(r.start), end: Number(r.end), purpose: r.purpose })) });
      toast.success('用途レンジを保存し、IPに適用しました');
      setRangesDialog(null);
      afterChange();
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
  };

  // VLAN dialogs (Omada) ──────────────────────────────────────────────────
  const numOrNull = (val) => (val !== '' && val != null ? Number(val) : null);
  const saveVlan = async () => {
    try {
      await itApi.updateVlan(editingVlan.id, { name: editingVlan.name || null, subnet: editingVlan.subnet || null, note: editingVlan.note || null });
      toast.success('VLAN情報を保存しました'); setEditingVlan(null); refreshVlans();
    } catch { toast.error('保存に失敗しました'); }
  };
  const saveVlanOmada = async () => {
    try {
      await itApi.vlanOmadaWrite(editingVlan.id, {
        name: editingVlan.name || null, vlan_id: numOrNull(editingVlan.vlan_id), gateway: editingVlan.gateway || null,
        prefix: numOrNull(editingVlan.prefix), dhcp_enabled: editingVlan.dhcp_enabled ?? null,
        dhcp_start: editingVlan.dhcp_start || null, dhcp_end: editingVlan.dhcp_end || null,
      });
      await itApi.updateVlan(editingVlan.id, { note: editingVlan.note || null });
      toast.success('Omadaへの反映を予約しました'); setEditingVlan(null); setTab('sync'); loadCommands(); refreshVlans();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Omada反映に失敗しました'); }
  };
  const openCreateVlan = () => setCreatingVlan({ site_id: sites[0] || '', name: '', vlan_id: '', gateway: '', prefix: 24, dhcp_enabled: false, dhcp_start: '', dhcp_end: '', note: '' });
  const createVlan = async () => {
    if (!creatingVlan.site_id || !creatingVlan.name || creatingVlan.vlan_id === '' || !creatingVlan.gateway) { toast.error('サイト・VLAN名・VLAN ID・ゲートウェイは必須です'); return; }
    try {
      await itApi.vlanOmadaCreate({
        site_id: creatingVlan.site_id, name: creatingVlan.name, vlan_id: Number(creatingVlan.vlan_id), gateway: creatingVlan.gateway,
        prefix: Number(creatingVlan.prefix || 24), dhcp_enabled: !!creatingVlan.dhcp_enabled,
        dhcp_start: creatingVlan.dhcp_start || null, dhcp_end: creatingVlan.dhcp_end || null, note: creatingVlan.note || null,
      });
      toast.success('新規VLAN作成を予約しました'); setCreatingVlan(null); setTab('sync'); loadCommands(); refreshVlans();
    } catch (e) { toast.error(e?.response?.data?.detail || 'VLAN作成に失敗しました'); }
  };

  // ── render: user view ─────────────────────────────────────────────────────
  if (!admin) return <UserVlanSummary vlans={vlans} />;

  // ── render: admin view ────────────────────────────────────────────────────
  return (
    <div className="space-y-5" data-testid="vlan-management">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">VLAN・IP管理</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="print-ip-plan-btn" onClick={printAll} className="h-10 rounded-lg">
            <Printer size={16} className="mr-1.5" /> PDF台帳
          </Button>
          <Button data-testid="new-vlan-btn" onClick={openCreateVlan} className="bg-teal-700 hover:bg-teal-800 h-10 rounded-lg">
            <Plus size={16} className="mr-1.5" /> 新規VLAN（Omada）
          </Button>
        </div>
      </div>

      <VlanPills vlans={vlans} activeVlan={activeVlan} onSelect={setActiveVlan} onEdit={(v) => setEditingVlan({ ...v })} />

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="vlan-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">概要</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list">一覧</TabsTrigger>
          <TabsTrigger value="sync" data-testid="tab-sync">
            Omada同期 {cmdPending > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px]">{cmdPending}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <OverviewTab
            activeVlan={activeVlan}
            allEntries={allEntries}
            loading={loading}
            mapMode={mapMode}
            setMapMode={setMapMode}
            onCellClick={openEditCell}
            onOpenRanges={openRanges}
          />
        </TabsContent>

        <TabsContent value="list" className="mt-4">
          <IpListTab
            allEntries={allEntries}
            loading={loading}
            unplaced={unplaced}
            selected={selected}
            setSelected={setSelected}
            onEdit={setEditing}
            onReserve={() => setReserve({ user: '', device_name: '', purpose: '' })}
          />
        </TabsContent>

        <TabsContent value="sync" className="mt-4">
          <OmadaSyncTab commands={commands} cmdPending={cmdPending} onRefresh={loadCommands} />
        </TabsContent>
      </Tabs>

      {selected.size > 0 && (
        <IpBulkBar
          count={selected.size}
          bulkOwner={bulkOwner}
          setBulkOwner={setBulkOwner}
          bulkPurpose={bulkPurpose}
          setBulkPurpose={setBulkPurpose}
          onApply={() => runBulk('set')}
          onRelease={() => runBulk('release')}
          onExport={exportSelected}
          onClear={() => setSelected(new Set())}
        />
      )}

      <IpEditDrawer entry={editing} onClose={() => setEditing(null)} onSave={saveEdit} onRelease={releaseFromDrawer} onOmadaWrite={writeToOmada} />
      <ReserveDialog reserve={reserve} setReserve={setReserve} vlanName={activeVlan?.name} onSubmit={submitReserve} />
      <VlanEditDialog editingVlan={editingVlan} setEditingVlan={setEditingVlan} onSave={saveVlan} onSaveOmada={saveVlanOmada} />
      <VlanCreateDialog creatingVlan={creatingVlan} setCreatingVlan={setCreatingVlan} sites={sites} onSubmit={createVlan} />
      <PurposeRangesDialog rangesDialog={rangesDialog} setRangesDialog={setRangesDialog} vlanName={activeVlan?.name} onSave={saveRanges} />
    </div>
  );
}
